/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { AutoAwesome, PriorityHigh } from '@mui/icons-material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import {
  AiInsightOrder,
  IncompleteRestockMetadataFragment,
  IncompleteTaskMetadataFragment,
  InsightPriority,
  PriceMismatchMetadataFragment,
  ProductNotSoldMetadataFragment,
  RecommendedSwapMetadataFragment,
  RevenueOutlierMetadataFragment,
  SortDirection,
  StoreInsightType,
} from 'graphql/generated-types'
import React from 'react'
import Markdown from 'react-markdown'

import IncompleteTaskInsight from '@/components/insights/IncompleteTaskInsight'
import InsightDialog from '@/components/insights/Insight.dialog'
import PriceMismatchInsight from '@/components/insights/PriceMismatchInsight'
import ProductNotSoldInsight from '@/components/insights/ProductNotSoldInsight'
import RecommendedSwapInsight from '@/components/insights/RecommendedSwapInsight'
import RevenueOutlierInsight from '@/components/insights/RevenueOutlierInsight'
import { InsightMetadata } from '@/components/insights/types'
import { useTableOrder } from '@/hooks/useTableOrder'
import { useTablePageState } from '@/hooks/useTablePageState'
import useT from '@/i18n/useT'
import DeliciousTable, { HeadCell } from '@/pages/components/DeliciousTable'

const renderSelectedInsight = (
  metadata: InsightMetadata,
  insightType: StoreInsightType,
): React.ReactNode | null => {
  switch (insightType) {
    case StoreInsightType.REVENUE_OUTLIER:
      return (
        <RevenueOutlierInsight
          metadata={metadata as RevenueOutlierMetadataFragment}
        />
      )
    case StoreInsightType.RECOMMENDED_SWAP:
      return (
        <RecommendedSwapInsight
          metadata={metadata as RecommendedSwapMetadataFragment}
        />
      )
    // Deprecated
    case StoreInsightType.INCOMPLETE_RESTOCK:
    case StoreInsightType.INCOMPLETE_TASK:
      return (
        <IncompleteTaskInsight
          metadata={
            metadata as
              | IncompleteRestockMetadataFragment
              | IncompleteTaskMetadataFragment
          }
        />
      )
    case StoreInsightType.PRICE_MISMATCH:
      return (
        <PriceMismatchInsight
          metadata={metadata as PriceMismatchMetadataFragment}
        />
      )
    case StoreInsightType.PRODUCT_NOT_SOLD:
      return (
        <ProductNotSoldInsight
          metadata={metadata as ProductNotSoldMetadataFragment}
        />
      )
    default:
      return null
  }
}

type TempAIInsightRow = {
  id: string
  priority: InsightPriority
  store: string
  storeId: string
  startDate: string
  endDate: string
  ids: string[]
  summary: string
  type: StoreInsightType
  metadata: InsightMetadata
}

const priorityMap = {
  [InsightPriority.HIGH]: 3,
  [InsightPriority.MEDIUM]: 2,
  [InsightPriority.LOW]: 1,
} as const

type ActionButtonProps = {
  onClick: () => void
  onAcknowledge: () => void
}

const ActionButton: React.FC<ActionButtonProps> = ({
  onClick,
  onAcknowledge,
}) => {
  const { t } = useT()
  const options = [t('aiInsightsTable.acknowledge')]
  const [open, setOpen] = React.useState(false)
  const anchorRef = React.useRef<HTMLDivElement>(null)

  const handleMenuItemClick = () => {
    onAcknowledge()
    setOpen(false)
  }

  const handleToggle = () => setOpen(prev => !prev)

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return
    }
    setOpen(false)
  }

  return (
    <>
      <ButtonGroup
        variant="contained"
        color="secondary"
        size="small"
        ref={anchorRef}
      >
        <Button onClick={onClick}>{t('aiInsightsTable.actionButton')}</Button>
        <Button onClick={handleToggle}>
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{ zIndex: 1 }}
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem>
                  {options.map(option => (
                    <MenuItem key={option} onClick={handleMenuItemClick}>
                      {option}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

export const rowsAllSparkling: TempAIInsightRow[] = [
  {
    id: 'temp-2',
    priority: InsightPriority.LOW,
    store: '',
    storeId: 'store-b',
    startDate: '2025-01-01',
    endDate: '2025-01-31',
    ids: ['insight-b1'],
    summary:
      'This cooler has a product that has been out of stock and also has a high revenue per facing.  Consider adjusting the planogram.',
    type: StoreInsightType.RECOMMENDED_SWAP,
    metadata: {
      __typename: 'RecommendedSwapMetadata',
      insightId: 'insight-b1',
      message:
        'Replacing a 1 facing of **Coca-Cola Bottle, 1 Liter** with **Coca-Cola Mexico Glass Bottle, 355 mL** is predicted to increase revenue.  Probability of improvement: 82.0%.  **Coca-Cola Bottle, 1 Liter** currently has 2 more facings than **Coca-Cola Mexico Glass Bottle, 355 mL**.',
      displayId: 'display-temp-1',
      oldUpc: '049000005226',
      oldCurrentFacings: 3,
      newUpc: '049000004632',
      newCurrentFacings: 1,
    } satisfies RecommendedSwapMetadataFragment,
  },
  // Replacing a few facings of Coca-Cola Bottle, 1 Liter with Coca-Cola Mexico Glass Bottle, 355 mL is predicted to increase revenue.
  // Probability of improvement: 82.0%.
  // Coca-Cola Bottle, 1 Liter currently has 2 more facings than Coca-Cola Mexico Glass Bottle, 355 mL.
]
export const rowsEnergyDrinks: TempAIInsightRow[] = [
  {
    id: 'temp-2',
    priority: InsightPriority.LOW,
    store: '',
    storeId: 'store-b',
    startDate: '2025-01-01',
    endDate: '2025-01-31',
    ids: ['insight-b1'],
    summary:
      'There is a new high revenue energy drink that is not currently in the cooler.  Consider adding it.',
    type: StoreInsightType.RECOMMENDED_SWAP,
    metadata: {
      __typename: 'RecommendedSwapMetadata',
      insightId: 'insight-b1',
      message:
        '**Monster Ultra Punk Punch 16oz Can** is selling well in other stores.  Replacing **Monster Ultra Fiesta 16oz Can** with **Monster Ultra Punk Punch 16oz Can** is predicted to increase revenue.  Probability of improvement: 91.0%.',
      displayId: 'display-temp-1',
      oldUpc: '070847034629',
      oldCurrentFacings: 2,
      newUpc: '070847899617',
      newCurrentFacings: 0,
    } satisfies RecommendedSwapMetadataFragment,
  },
]

const CoolerTempAIInsightsTable: React.FC<{ coolerName: string }> = ({
  coolerName,
}) => {
  const { t } = useT()
  const { page, rowsPerPage, setPage, setRowsPerPage } = useTablePageState({
    initialRowsPerPage: 5,
  })
  const { order, handleRequestSort, orderBy } = useTableOrder<AiInsightOrder>(
    'priority',
    SortDirection.ASC,
  )

  const [selectedInsight, setSelectedInsight] =
    React.useState<TempAIInsightRow | null>(null)
  const [acknowledgedIds, setAcknowledgedIds] = React.useState<string[]>([])

  const rowsAll =
    coolerName === 'Cold Vault 1'
      ? rowsAllSparkling
      : coolerName === 'Cold Vault 2'
        ? rowsEnergyDrinks
        : []

  const rowsFiltered = React.useMemo(() => {
    return rowsAll.filter(
      row => !row.ids.every(id => acknowledgedIds.includes(id)),
    )
  }, [rowsAll, acknowledgedIds])

  const rowsSorted = React.useMemo(() => {
    const dir = order === SortDirection.ASC ? 1 : -1
    const valFor = (row: TempAIInsightRow) => {
      switch (orderBy) {
        case 'priority':
          return priorityMap[row.priority]
        case 'store':
          return row.store
        case 'type':
          return String(row.type)
        default:
          return 0
      }
    }

    const cmp = (a: TempAIInsightRow, b: TempAIInsightRow) => {
      const va = valFor(a)
      const vb = valFor(b)
      if (typeof va === 'number' && typeof vb === 'number') {
        return (va - vb) * dir
      }
      return String(va).localeCompare(String(vb)) * dir
    }

    return [...rowsFiltered].sort(cmp)
  }, [order, orderBy, rowsFiltered])

  React.useEffect(() => {
    // When sorting changes, ensure we don't end up on an empty page.
    setPage(0)
  }, [order, orderBy, setPage])

  const totalCount = rowsSorted.length
  const startIdx = page * rowsPerPage
  const endIdx = startIdx + rowsPerPage
  const rows = rowsSorted.slice(startIdx, endIdx)

  const renderRow = (row: TempAIInsightRow, index: number) => {
    return (
      <TableRow key={row.id || index} hover tabIndex={-1} role="checkbox">
        <TableCell width={100}>
          <Stack direction="row" spacing={-2} ml={-2}>
            {Array.from({ length: priorityMap[row.priority] }).map((_, idx) => (
              <PriorityHigh
                key={`priority-icon-${idx}`}
                sx={theme => ({ color: theme.palette.error.dark })}
              />
            ))}
          </Stack>
        </TableCell>
        <TableCell>
          <Typography variant="body2" component="div">
            <Markdown>{row.summary}</Markdown>
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {t('aiInsightsTable.insightType', { type: row.type })}
          </Typography>
        </TableCell>
        <TableCell>
          <ActionButton
            onClick={() => setSelectedInsight(row)}
            onAcknowledge={() => {
              setAcknowledgedIds(prev => [...prev, ...row.ids])
              setPage(0)
            }}
          />
        </TableCell>
      </TableRow>
    )
  }

  return (
    <>
      <DeliciousTable<TempAIInsightRow, AiInsightOrder>
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesome />
            <span>{t('aiInsightsTable.title')}</span>
          </Stack>
        }
        noDataMessage={t('aiInsightsTable.noData')}
        data={rows}
        headCells={[
          {
            id: 'priority',
            alignment: 'left',
            label: t('aiInsightsTable.priorityHeader'),
            sortKey: 'priority',
          } as HeadCell<AiInsightOrder>,
          {
            id: 'message',
            alignment: 'left',
            label: t('aiInsightsTable.messageHeader'),
          } as HeadCell<AiInsightOrder>,
          {
            id: 'type',
            alignment: 'left',
            label: t('aiInsightsTable.typeHeader'),
            sortKey: 'type',
          } as HeadCell<AiInsightOrder>,
          {
            id: 'action',
            alignment: 'left',
            label: t('aiInsightsTable.actionHeader'),
          } as HeadCell<AiInsightOrder>,
        ]}
        renderRow={renderRow}
        loading={false}
        totalCount={totalCount}
        order={order === 'ASC' ? 'asc' : 'desc'}
        orderBy={orderBy}
        handleRequestSort={handleRequestSort}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        paperSx={{
          height: 340,
          display: 'flex',
          flexDirection: 'column',
        }}
        tableContainerSx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'auto',
        }}
        tableSx={{
          flex: 1,
        }}
      />
      {selectedInsight && (
        <InsightDialog
          open={Boolean(selectedInsight)}
          onClose={() => {
            setSelectedInsight(null)
          }}
          renderFrame={meta =>
            renderSelectedInsight(meta, selectedInsight.type)
          }
          onAcknowledge={() => {
            if (!selectedInsight) return
            setAcknowledgedIds(prev => [...prev, ...selectedInsight.ids])
            setSelectedInsight(null)
            setPage(0)
          }}
          acknowledgedIds={acknowledgedIds}
          metadata={selectedInsight.metadata ? [selectedInsight.metadata] : []}
          title={`${coolerName} Insight`}
        />
      )}
    </>
  )
}

export default CoolerTempAIInsightsTable
